import React, { useCallback, useMemo, useState } from 'react';
import { FiservCardInterface } from '@common-fe/common-fe';

import { CommunicationChannels, Roles } from '@/common/constants';
import VerifyMyCardModal from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal';
import { GoPaperlessBanner } from '@/modules/core/components/Dashboard/components/BannersList/GoPaperlessBanner';
import { useHasAccess } from '@/modules/core/hooks';
import { useOrphanModeStore } from '@/modules/core/store';
import { TaxFormBanner } from '@/modules/investments';
import { AutoInvestmentBanner, useAutoInvestmentBanner } from '@/modules/investments/AutoInvestment';
import { useTaxFormBanner } from '@/modules/investments/TaxForms/hooks';
import { CardReplaceModal } from '@/modules/transaction/components/Cards/components/CardReplaceModal/CardReplaceModal.tsx';
import { ReplacementInitiationType } from '@/modules/transaction/components/Cards/components/CardReplaceModal/hooks/useCardReplacement.query';
import useCardReplaceModalStore from '@/modules/transaction/components/Cards/store/useCardReplaceModal.store.ts';
import { GoPaperless } from '@/modules/transaction/components/Statements/components/GoPaperless';
import { useMyEnrollments, useOrganizationProperties } from '@/modules/transaction/hooks';
import { useGetCardsQuery } from '@/modules/transaction/queries/useGetCards.query';
import useGetBeneficiariesQuery from '@/modules/user/components/ManageProfile/components/Beneficiaries/useGetBeneficiaries.query';
import { PhoneVerificationModal } from '@/modules/user/components/ManageProfile/components/CommunicationPreferences/components/PhoneVerificationModal';
import { useGetLinkedAccountsQuery } from '@/modules/user/components/UserDrodpdown/components/OrphanModeSwitcher/queries';
import { useAuthStore } from '@/modules/user/stores';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';
import { StatementType } from '@/modules/user/user.types';

import { BalanceReminderBanners } from '../../BalanceReminderBanners';
import useDismissBalanceReminderStore from '../../BalanceReminderBanners/useDismissBalanceReminder.store';
import useGetBalanceReminderQuery from '../../BalanceReminderBanners/useGetBalanceReminder.query';
import { BannerPlaidVerification } from '../BannerPlaidVerification';
import { PlaidBannerModals } from '../BannerPlaidVerification/PlaidBannerModals';
import useGetDataForBannerPlaidVerification from '../BannerPlaidVerification/useGetDataForBannerPlaidVerification';
import { BannerStorageKey } from '../bannersList.constants';
import { BeneficiaryBanner } from '../BeneficiaryBanner';
import CompleteBanner from '../CompleteBanner';
import { ImportantChangesBanner } from '../ImportantChangesBanner';
import { LinkedEmployeeBanner } from '../LinkedEmployeeBanner';
import { StayInTheKnowBanner } from '../StayInTheKnowBanner';
import SwitchEmployeeBanner from '../SwitchEmployeeBanner/SwitchEmployeeBanner';
import useIsDisplayCompleteRegistrationBanner from '../SwitchEmployeeBanner/useIsDisplayCompleteRegistrationBanner.ts';

import useDismissBanner from './useDismissBanner';

export interface SlideItem {
  node: React.ReactNode;
  id: number;
}

const isCardNotVerified = (card?: FiservCardInterface) => card && !card.isVerified;

const useBannersList = (): SlideItem[] => {
  const {
    formattedData: { thirtyDaysBeforeBanner },
    isLoading,
  } = useGetBalanceReminderQuery();
  const { isDismissed } = useDismissBalanceReminderStore();
  const hasBalanceReminderBanner = useMemo(
    () => !isLoading && !isDismissed && thirtyDaysBeforeBanner,
    [isDismissed, isLoading, thirtyDaysBeforeBanner]
  );
  const { user } = useAuthStore();
  const { formattedAccount: bannerPlaidVerificationData, refetch: bankAccountRefetch } =
    useGetDataForBannerPlaidVerification(user?.employee?.id);
  const { linkedEmployee } = useOrphanModeStore();
  const { employee } = useEmployeeStore();
  const { organizationProperties } = useOrganizationProperties();
  const [isVerifyMyCardModalVisible, setIsVerifyMyCardModalVisible] = useState(false);
  const [isGoPaperlessModalVisible, setIsGoPaperlessModalVisible] = useState(false);
  const [isStayInTheKnowModalVisible, setIsStayInTheKnowModalVisible] = useState(false);
  const { refetch, employeePendingOpenCard, employeeActiveCard } = useGetCardsQuery(employee?.id);
  const {
    state: { modalVisible: isCompleteBannerModalVisible, cardholderName },
    handleChangeVisibility,
  } = useCardReplaceModalStore();

  const handleOpenVerifyMyCardModal = useCallback(() => setIsVerifyMyCardModalVisible(true), []);
  const handleOpenGoPaperlessModal = useCallback(() => setIsGoPaperlessModalVisible(true), []);
  const handleOpenStayInTheKnowModal = useCallback(() => setIsStayInTheKnowModalVisible(true), []);

  const employeeHasLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);

  const isSsoUser = useMemo(() => user?.isSsoUser, [user]);
  const showCompleteBanner = useMemo(() => {
    return (
      (isCardNotVerified(employeePendingOpenCard) || isCardNotVerified(employeeActiveCard)) &&
      employeeHasLimitedAccess &&
      !isSsoUser
    );
  }, [employeePendingOpenCard, employeeActiveCard, employeeHasLimitedAccess, isSsoUser]);

  const { checkIsBannerDismissed } = useDismissBanner();

  const isImportantChangesBannerDismissed = useMemo(
    () => checkIsBannerDismissed(BannerStorageKey.ImportantChangesBanner),
    [checkIsBannerDismissed]
  );

  const isGoPaperlessBannerDismissed = useMemo(
    () => checkIsBannerDismissed(BannerStorageKey.GoPaperlessBanner),
    [checkIsBannerDismissed]
  );

  const isLinkedEmployeeBannerDismissed = useMemo(
    () => checkIsBannerDismissed(BannerStorageKey.LinkedEmployeeBanner),
    [checkIsBannerDismissed]
  );

  const isBeneficiaryBannerDismissed = useMemo(
    () => checkIsBannerDismissed(BannerStorageKey.BeneficiaryBanner),
    [checkIsBannerDismissed]
  );

  const isStayInTheKnowBannerDismissed = useMemo(
    () => checkIsBannerDismissed(BannerStorageKey.StayInTheKnowBanner),
    [checkIsBannerDismissed]
  );

  const showImportantChangesBanner = useMemo(
    () => !linkedEmployee && !isImportantChangesBannerDismissed,
    [linkedEmployee, isImportantChangesBannerDismissed]
  );

  const isPaperAndElectronicsStatementType = useMemo(
    () => organizationProperties.statementType === StatementType.PAPER_AND_ELECTRONIC,
    [organizationProperties]
  );

  const showGoPaperlessBanner = useMemo(
    () =>
      !isGoPaperlessBannerDismissed &&
      isPaperAndElectronicsStatementType &&
      employee &&
      !employee?.isElectronicStatementOnly,
    [isGoPaperlessBannerDismissed, isPaperAndElectronicsStatementType, employee]
  );

  const { formattedData: linkedEmployees } = useGetLinkedAccountsQuery();
  const hasLinkedEmployeeBanner = useMemo(
    () =>
      Boolean(
        !isLinkedEmployeeBannerDismissed &&
          employee &&
          !employee?.isCreatedByOrphan &&
          linkedEmployees?.length
      ),
    [employee, isLinkedEmployeeBannerDismissed, linkedEmployees]
  );

  const { hasOpenHSA } = useMyEnrollments();

  const { formattedData: beneficiaries } = useGetBeneficiariesQuery(hasOpenHSA);

  const hasBeneficiaryBanner = useMemo(
    () => !isBeneficiaryBannerDismissed && hasOpenHSA && !beneficiaries?.length,
    [hasOpenHSA, beneficiaries, isBeneficiaryBannerDismissed]
  );

  const { isDisplayedBanner: isAutoInvestmentBannerDisplayed } = useAutoInvestmentBanner();

  const { isDisplayed: isTaxFormDisplayedBanner, handleDismiss: handleDismissTaxBanner } = useTaxFormBanner();
  const isCompleteYourRegistrationBannerDisplayed = useIsDisplayCompleteRegistrationBanner();

  const isSmsNotificationDisabled = useMemo(() => !employee?.isSmsNotificationEnabled, [employee]);
  const hasEmailSMSCommunicationChannel = useMemo(
    () =>
      organizationProperties?.communicationChannels?.length &&
      organizationProperties?.communicationChannels?.includes(CommunicationChannels.EMAIL_SMS),
    [organizationProperties]
  );

  const hasStayInTheKnowBanner = useMemo(
    () =>
      (isSmsNotificationDisabled &&
        hasEmailSMSCommunicationChannel &&
        !isStayInTheKnowBannerDismissed &&
        !employeeHasLimitedAccess) ||
      isStayInTheKnowModalVisible,
    [
      isSmsNotificationDisabled,
      hasEmailSMSCommunicationChannel,
      isStayInTheKnowBannerDismissed,
      isStayInTheKnowModalVisible,
      employeeHasLimitedAccess,
    ]
  );

  const bannersList = useMemo(
    () =>
      [
        ...(showCompleteBanner
          ? [
              {
                id: 1, // Banner Type "Limited Access". Priority 1.
                height: 248,
                node: <CompleteBanner handleOpenModal={handleOpenVerifyMyCardModal} />,
                modalWindow: isVerifyMyCardModalVisible ? (
                  <VerifyMyCardModal onSetVisible={setIsVerifyMyCardModalVisible} refreshCardList={refetch} />
                ) : (
                  <CardReplaceModal
                    visible={isCompleteBannerModalVisible}
                    cardholderName={cardholderName}
                    onSetVisible={handleChangeVisibility}
                    refreshCardList={refetch}
                    replacementInitiationType={ReplacementInitiationType.BANNER}
                  />
                ),
              },
            ]
          : []),

        ...(hasBalanceReminderBanner
          ? [
              {
                id: 2, // Banner Type "End of Year Balance Reminder". Priority 2.
                node: <BalanceReminderBanners />,
              },
            ]
          : []),

        ...(showImportantChangesBanner && !isSsoUser && employee?.isCreatedByOrphan
          ? [
              {
                id: 3, // Banner Type "HSA Orphan". Priority 3.
                node: <ImportantChangesBanner />,
              },
            ]
          : []),

        ...(isCompleteYourRegistrationBannerDisplayed
          ? [
              {
                id: 4, // Orphan - Complete your registration. Priority 4.
                node: <SwitchEmployeeBanner />,
              },
            ]
          : []),

        ...(bannerPlaidVerificationData
          ? [
              {
                id: 5, // Banner Type "Payment Verification Pending". Priority 5.
                node: (
                  <BannerPlaidVerification data={bannerPlaidVerificationData} refetch={bankAccountRefetch} />
                ),
                modalWindow: <PlaidBannerModals refetch={bankAccountRefetch} />,
              },
            ]
          : []),

        ...(showGoPaperlessBanner && !isSsoUser
          ? [
              {
                id: 6, // Banner Type "Go Paperless". Priority 6.
                node: <GoPaperlessBanner handleOpenModal={handleOpenGoPaperlessModal} />,
                modalWindow: isGoPaperlessModalVisible && (
                  <GoPaperless onSetVisible={setIsGoPaperlessModalVisible} />
                ),
              },
            ]
          : []),

        ...(isAutoInvestmentBannerDisplayed
          ? [
              {
                id: 7, // Banner Type "Auto-Investments". Priority 7.
                node: <AutoInvestmentBanner />,
                modalWindow: false,
              },
            ]
          : []),

        ...(hasBeneficiaryBanner
          ? [
              {
                id: 8, // Banner Type "Beneficiary". Priority 8.
                node: <BeneficiaryBanner />,
              },
            ]
          : []),

        ...(isTaxFormDisplayedBanner
          ? [
              {
                id: 9, // Banner Type "Tax form". Priority 9.
                node: <TaxFormBanner onDismiss={handleDismissTaxBanner} />,
                modalWindow: false,
              },
            ]
          : []),

        ...(hasLinkedEmployeeBanner
          ? [
              {
                id: 10, // Banner Type "Multiple Linked Accounts". Priority 10.
                node: <LinkedEmployeeBanner />,
              },
            ]
          : []),

        ...(hasStayInTheKnowBanner
          ? [
              {
                id: 11, // Banner Type "Stay in the Know". Priority 11.
                node: <StayInTheKnowBanner openStayInTheKnowModal={handleOpenStayInTheKnowModal} />,
                ...(isStayInTheKnowModalVisible
                  ? {
                      modalWindow: (
                        <PhoneVerificationModal
                          employeePhoneNumber={employee?.phone?.number}
                          employerPhoneNumber={employee?.employerProvidedPhone?.number}
                          onSetVisible={setIsStayInTheKnowModalVisible}
                        />
                      ),
                    }
                  : {}),
              },
            ]
          : []),
      ].sort((a, b) => a.id - b.id),
    [
      hasBeneficiaryBanner,
      isCompleteYourRegistrationBannerDisplayed,
      employee,
      hasLinkedEmployeeBanner,
      bannerPlaidVerificationData,
      showImportantChangesBanner,
      isSsoUser,
      showCompleteBanner,
      handleOpenVerifyMyCardModal,
      isVerifyMyCardModalVisible,
      isCompleteBannerModalVisible,
      cardholderName,
      handleChangeVisibility,
      refetch,
      isTaxFormDisplayedBanner,
      handleDismissTaxBanner,
      isAutoInvestmentBannerDisplayed,
      showGoPaperlessBanner,
      handleOpenGoPaperlessModal,
      isGoPaperlessModalVisible,
      hasBalanceReminderBanner,
      bankAccountRefetch,
      hasStayInTheKnowBanner,
      isStayInTheKnowModalVisible,
      handleOpenStayInTheKnowModal,
    ]
  );

  return bannersList;
};

export default useBannersList;
